import { Ref, ref, computed } from "vue";
import { KoyouKeiyakuService } from "@/service/KoyouKeiyakuService";
import { KoyouKeiyakuOption, KoyouKeiyakuTime } from "@/types/KoyouKeiyaku"
import { KoyouKeiyakuDetail } from "@/types/KoyouKeiyakuDetail";
import { useKoyouKeiyakuSearchStore } from '@/store/koyoukeiyaku-search'
import { BaseOptionWithoutCode } from "@/types/Option";
import OptionConst from "@/const/Option";
import { useAuthStore } from '@/store/auth';
import { PermissionConst } from "@/const/Permission";
import { Flow } from "@/types/SyouninGroup";

/*
* 初期処理
*/
export function useKoyouKeiyakuInit() {
  const loading = ref(0) // APIで読み込みをする本数
  const isError = ref(false)

  // 雇用契約ストアから検索条件を取得
  const searchStore = useKoyouKeiyakuSearchStore();

  // 雇用契約の検索で必要なオプション類を取得する
  const koyouKeiyakuService = new KoyouKeiyakuService();
  const options: Ref<KoyouKeiyakuOption | null> = ref(null);

  // 書面タイトル
  const sheetTitle = ref("");

  // ログインユーザーが本人承認ステータスを更新できるか
  let honninSyouninUserIds: number[] = [];

  // ログインユーザーが雇用契約を削除できるか
  let deleteUserIds: number[] = [];
  const canDelete: Ref<boolean> = ref(false);

  const getAllOptions = async () => {
    loading.value++
    const res = await koyouKeiyakuService.getAllOptions()
    if (!res) {
      isError.value = true
    }

    options.value = res
    // Create.vue（入社タイプ）の為の申請種類オプション
    loading.value--
  }

  const shinseiTypeOptionsForCreate = computed(() => {
    if (!options.value) {
      return []
    }
    return options.value.shinsei_type.filter(opt =>
      opt.val === OptionConst.KOYOU_SHINSEI_TYPE.ENTRY ||
      opt.val === OptionConst.KOYOU_SHINSEI_TYPE.ADD
    )
  })


  const koyouTypeOptionsForHaken = computed(() => {
    if (!options.value) {
      return []
    }
    return options.value.koyou_type.filter(opt =>
      opt.val === OptionConst.KOYOU_TYPE.GEKKYUU || opt.val === OptionConst.KOYOU_TYPE.PART
    )
  })

  // 選択された雇用形態から、通知書タイプに応じたコンポーネント名を返す
  const setKoyouKeiyakuTemplate = async (keiyakuData: KoyouKeiyakuDetail) => {
    if (!options.value) {
      return
    }
    const shinseiTypeLabel = getOptionsLabel(options.value.shinsei_type, keiyakuData.shinsei_type);
    const koyouTypeLabel = getOptionsLabel(options.value.koyou_type, keiyakuData.koyou_type);
    sheetTitle.value = shinseiTypeLabel + '（' + koyouTypeLabel + '）';
  }

  // Option（val/label）のlabel文字列を取得
  const getOptionsLabel = (options: BaseOptionWithoutCode[], val: number | null | undefined) => {
    if (val === null || typeof val === 'undefined') {
      return '-'
    }
    const label = options.find((opt) => opt.val === val)?.label;
    if (typeof label === 'undefined') {
      return '-'
    } else {
      return label
    }
  }

  /**
   * ログインユーザーが本人承認ステータスを更新できるかを返す
   */
  const canHonninSyounin = ref(false)
  const judgeHoninSyounin = async (keiyakuData: KoyouKeiyakuDetail, flow: Flow[]) => {
    // 本人承認ステータスを更新できるのは雇用契約の編集パーミッション、営業担当、提出者とする
    const authStore = useAuthStore();

    // 社内承認がされていない場合は本人承認ステータスの更新は行えない
    if (!keiyakuData.syounin_status_type || keiyakuData.syounin_status_type !== OptionConst.SYOUNIN_STATUS.APPROVED) {
      canHonninSyounin.value = false
      return
    }

    // 操作ユーザーが雇用契約の作成／編集パーミッションを持っていない場合はNG
    if (
      !authStore.hasPermission(PermissionConst.KOYOUKEIYAKU_CREATE_FOR_ALL) &&
      !authStore.hasPermission(PermissionConst.KOYOUKEIYAKU_CREATE)
    ) {
      canHonninSyounin.value = false
      return
    }

    const loginUserId = authStore.user?.user_id;
    if (!loginUserId) {
      canHonninSyounin.value = false
      return
    }

    // 営業と提出者はOK
    if (
      keiyakuData.eigyou_user_id === loginUserId ||
      keiyakuData.submitted_user_id === loginUserId
    ) {
      canHonninSyounin.value = true
      return
    }

    // 管理者はOK
    if (authStore.isAdmin) {
      canHonninSyounin.value = true
      return
    }

    // 承認フローに含まれればOK
    const result = flow.find(f => f.user_id === loginUserId)

    canHonninSyounin.value = typeof result !== 'undefined' ? true : false
  }

  /**
   * ログインユーザーが雇用契約を削除できるかを返す
   */
  const judgeDelete = async (keiyakuData: KoyouKeiyakuDetail) => {
    canDelete.value = false;
    // 削除できるユーザーの条件（MEDICA_AS-518）
    // 削除パーミッションを持っている（A～C共通）
    // A 雇用契約書の提出前...created_by、updated_by、担当営業　
    // B 雇用契約書の承認待ち中...提出者、担当営業　（「取り下げる」ボタン）
    // C 承認処理後...削除パーミッションのみ

    const authStore = useAuthStore();

    if (keiyakuData.is_sakujo) {
      // すでに削除されている
      canDelete.value = false
      return
    }

    // 削除パーミッション（A～C共通）
    if (authStore.hasPermission(PermissionConst.KOYOUKEIYAKU_CREATE)) {
      canDelete.value = true
    }
    else if (authStore.hasPermission(PermissionConst.KOYOUKEIYAKU_CREATE) && keiyakuData.syozoku_id === authStore.user?.mst_syozoku_id) {
      canDelete.value = true
    }
    else {
      return
    }

    const loginUserId = authStore.user?.user_id;

    // A 雇用契約書の提出前...created_by、updated_by、担当営業　
    if (keiyakuData.submitted_date === null) {
      if (keiyakuData.created_by) {
        deleteUserIds.push(keiyakuData.created_by)
      }
      if (keiyakuData.updated_by) {
        deleteUserIds.push(keiyakuData.updated_by)
      }
      if (keiyakuData.eigyou_user_id) {
        deleteUserIds.push(keiyakuData.eigyou_user_id)
      }
      // ログインユーザーが本人承認できるか判定
      if (deleteUserIds.length > 0 && loginUserId) {
        canDelete.value = deleteUserIds.includes(loginUserId)
      }
    }

    // B 雇用契約書の承認待ち中...提出者、担当営業　（「取り下げる」ボタン）
    if (keiyakuData.syounin_status_type === OptionConst.SYOUNIN_STATUS.WAIT) {
      if (keiyakuData.submitted_user_id) {
        deleteUserIds.push(keiyakuData.submitted_user_id)
      }
      if (keiyakuData.eigyou_user_id) {
        deleteUserIds.push(keiyakuData.eigyou_user_id)
      }
      // ログインユーザーが削除できるか判定
      if (deleteUserIds.length > 0 && loginUserId) {
        canDelete.value = deleteUserIds.includes(loginUserId)
      }
    }
    return canDelete
  }

  
  /**
   * 曜日を配列にして返します
   * @param v
   * @returns
   */
  const getYoubiLabel = (v: KoyouKeiyakuTime | null) => {
    if (!v) {
      return []
    }
    let res = []
    if(v.is_monday){
      res.push(OptionConst.YOUBI_LABEL.MONDAY)
    }
    if(v.is_tuesday){
      res.push(OptionConst.YOUBI_LABEL.TUESDAY)
    }
    if(v.is_wednesday){
      res.push(OptionConst.YOUBI_LABEL.WEDNESDAY)
    }
    if(v.is_thursday){
      res.push(OptionConst.YOUBI_LABEL.THURSDAY)
    }
    if(v.is_friday){
      res.push(OptionConst.YOUBI_LABEL.FRIDAY)
    }
    if(v.is_saturday){
      res.push(OptionConst.YOUBI_LABEL.SATURDAY)
    }
    if(v.is_sunday){
      res.push(OptionConst.YOUBI_LABEL.SUNDAY)
    }
    return res
  }

  // 読み込み中か判断する
  const isLoading = computed(() => {
    return !(loading.value === 0 || isError.value)
  })
  const searchCondition = computed(() => {
    return searchStore.condition
  })

  return {
    isLoading,
    isError,
    searchCondition,
    getAllKoyouKeiyakuOption: getAllOptions,
    options,
    shinseiTypeOptionsForCreate,
    koyouTypeOptionsForHaken,
    setKoyouKeiyakuTemplate,
    sheetTitle,
    getOptionsLabel,
    judgeHoninSyounin,
    canHonninSyounin,
    judgeDelete,
    canDelete,
    getYoubiLabel
  }
}
